import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Sparkles, Power, Search, XCircle, RefreshCw, ExternalLink } from 'lucide-react';

const STATUS_LABELS = {
    requested: 'Solicitado', picked_up: 'Recolectado', processing: 'En proceso',
    ready: 'Listo', out_for_delivery: 'En entrega', delivered: 'Entregado', cancelled: 'Cancelado',
};

export default function AdminServices() {
    const [tab, setTab] = useState('providers');
    const [providers, setProviders] = useState([]);
    const [orders, setOrders] = useState([]);
    const [summary, setSummary] = useState([]);
    const [q, setQ] = useState('');
    const [status, setStatus] = useState('all');
    const [orderQ, setOrderQ] = useState('');
    const [orderStatus, setOrderStatus] = useState('all');
    const [orderKind, setOrderKind] = useState('all');

    const loadProviders = useCallback(async () => {
        try {
            const { data } = await api.get('/admin/services/providers', { params: { q, status, limit: 100 } });
            setProviders(data.items || []);
        } catch (e) { toast.error('Error'); }
    }, [q, status]);

    const loadOrders = useCallback(async () => {
        try {
            const params = { limit: 200 };
            if (orderQ) params.q = orderQ;
            if (orderStatus !== 'all') params.status = orderStatus;
            if (orderKind !== 'all') params.kind = orderKind;
            const { data } = await api.get('/admin/services/orders', { params });
            setOrders(data.items || []);
            setSummary(data.summary || []);
        } catch (e) { toast.error('Error'); }
    }, [orderQ, orderStatus, orderKind]);

    useEffect(() => { if (tab === 'providers') loadProviders(); }, [tab, loadProviders]);
    useEffect(() => { if (tab === 'orders') loadOrders(); }, [tab, loadOrders]);

    const toggleProvider = async (pid) => {
        try {
            await api.post(`/admin/services/providers/${pid}/toggle`);
            toast.success('Estado actualizado');
            loadProviders();
        } catch (e) { toast.error('Error'); }
    };

    const forceCancel = async (oid) => {
        if (!window.confirm('¿Cancelar forzadamente esta orden? Se reembolsará el pago al cliente.')) return;
        try {
            await api.post(`/admin/services/orders/${oid}/cancel`);
            toast.success('Orden cancelada y reembolsada');
            loadOrders();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    return (
        <div className="space-y-6" data-testid="admin-services-page">
            <div className="flex items-center gap-2">
                <Sparkles className="h-6 w-6 text-[hsl(var(--primary))]" />
                <h1 className="text-2xl font-semibold">Servicios (Lavandería / Tintorería)</h1>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="providers" data-testid="admin-svc-tab-providers">Prestadores</TabsTrigger>
                    <TabsTrigger value="orders" data-testid="admin-svc-tab-orders">Órdenes</TabsTrigger>
                </TabsList>

                <TabsContent value="providers" className="pt-4 space-y-3">
                    <Card><CardContent className="p-3 flex flex-wrap gap-2">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre" className="pl-9" />
                        </div>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                <SelectItem value="active">Activos</SelectItem>
                                <SelectItem value="inactive">Inactivos</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button variant="outline" onClick={loadProviders}><RefreshCw className="h-4 w-4" /></Button>
                    </CardContent></Card>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                        {providers.length === 0 && <div className="col-span-full text-sm text-muted-foreground py-10 text-center">Sin prestadores.</div>}
                        {providers.map(p => (
                            <Card key={p.id} data-testid={`admin-svc-provider-${p.id}`}>
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="font-semibold truncate">{p.name}</div>
                                            <div className="text-xs text-muted-foreground">@{p.nickname} · {p.kind}</div>
                                            <div className="text-xs mt-1">{p.zones?.slice(0, 3).join(', ')}</div>
                                            <div className="text-xs text-muted-foreground mt-1">Pedidos: <strong>{p.orders_count}</strong> · tarifas: recolección ${p.pickup_fee} / entrega ${p.delivery_fee}</div>
                                        </div>
                                        {p.active ? <Badge className="bg-emerald-100 text-emerald-800">Activo</Badge> : <Badge variant="outline">Inactivo</Badge>}
                                    </div>
                                    <div className="flex gap-2 mt-3">
                                        <Button size="sm" variant={p.active ? 'outline' : 'default'} className="flex-1" onClick={() => toggleProvider(p.id)}>
                                            <Power className="h-3.5 w-3.5 mr-1" />{p.active ? 'Desactivar' : 'Activar'}
                                        </Button>
                                        <a href={`/servicios/prestador/${p.id}`} target="_blank" rel="noreferrer">
                                            <Button size="sm" variant="ghost"><ExternalLink className="h-3.5 w-3.5" /></Button>
                                        </a>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </TabsContent>

                <TabsContent value="orders" className="pt-4 space-y-3">
                    {summary.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                            {summary.map(s => (
                                <Card key={s._id}><CardContent className="p-3">
                                    <div className="text-[10px] uppercase text-muted-foreground">{STATUS_LABELS[s._id] || s._id}</div>
                                    <div className="font-semibold">{s.count}</div>
                                    <div className="text-xs text-muted-foreground">${(s.sum || 0).toFixed(0)}</div>
                                </CardContent></Card>
                            ))}
                        </div>
                    )}
                    <Card><CardContent className="p-3 flex flex-wrap gap-2">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input value={orderQ} onChange={(e) => setOrderQ(e.target.value)} placeholder="Buscar por nickname" className="pl-9" />
                        </div>
                        <Select value={orderStatus} onValueChange={setOrderStatus}>
                            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={orderKind} onValueChange={setOrderKind}>
                            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                <SelectItem value="laundry">Lavandería</SelectItem>
                                <SelectItem value="drycleaning">Tintorería</SelectItem>
                            </SelectContent>
                        </Select>
                    </CardContent></Card>
                    <div className="rounded-lg border overflow-hidden">
                        <div className="grid grid-cols-12 text-[10px] uppercase text-muted-foreground bg-[hsl(var(--secondary))] px-3 py-2">
                            <div className="col-span-2">#Orden</div>
                            <div className="col-span-2">Comprador</div>
                            <div className="col-span-2">Prestador</div>
                            <div className="col-span-1">Tipo</div>
                            <div className="col-span-2">Estado</div>
                            <div className="col-span-2">Total</div>
                            <div className="col-span-1 text-right">Acción</div>
                        </div>
                        {orders.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center">Sin órdenes.</div>}
                        {orders.map(o => (
                            <div key={o.id} className="grid grid-cols-12 items-center px-3 py-2 border-t text-sm hover:bg-[hsl(var(--accent))]/30">
                                <div className="col-span-2 font-mono text-xs">#{o.id.slice(0, 8)}</div>
                                <div className="col-span-2 truncate">@{o.buyer_nickname}</div>
                                <div className="col-span-2 truncate">@{o.provider_nickname}</div>
                                <div className="col-span-1"><Badge variant="outline" className="text-[10px]">{o.kind === 'drycleaning' ? 'Tintorería' : 'Lavandería'}</Badge></div>
                                <div className="col-span-2"><Badge className="text-[10px]">{STATUS_LABELS[o.status]}</Badge></div>
                                <div className="col-span-2 font-semibold">${o.total} <span className="text-[10px] text-muted-foreground">({o.payment_status})</span></div>
                                <div className="col-span-1 text-right">
                                    {!['delivered', 'cancelled'].includes(o.status) && (
                                        <Button size="sm" variant="ghost" onClick={() => forceCancel(o.id)} title="Cancelar y reembolsar">
                                            <XCircle className="h-4 w-4 text-destructive" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
